// Chatwoot live-chat — ports psychologist.js initPsychologistChat/openPsychologistChat.
// Lazy-loads the SDK once, identifies the user, and toggles the widget open.

import { useCallback, useRef } from 'react'
import { useAuthStore } from '../store/authStore'
import { useChatwootStore } from '../store/chatwootStore'
import { chatwootApi } from '../api/chatwoot'
import { toast } from '../store/toastStore'
import * as metrica from '../lib/metrica'

export function useChatwoot() {
  const loadedRef = useRef(false)

  const init = useCallback(async () => {
    if (loadedRef.current) {
      window.$chatwoot?.toggle('open')
      return
    }
    const { userId, displayName } = useAuthStore.getState()
    try {
      const { base_url, website_token, identifier_hash } = await chatwootApi.token()

      // SDK already initialized from a previous session (e.g. after logout →
      // login)? $chatwoot exists but `chatwoot:ready` won't re-fire, so set the
      // user directly — otherwise the widget opens with a stale/anonymous
      // identity and starts a fresh conversation instead of resuming the
      // existing one (parent bd-eiw).
      if (window.$chatwoot) {
        window.$chatwoot.setUser(userId ?? '', { identifier_hash, name: displayName || '' })
        loadedRef.current = true
        metrica.reachGoal('psychologist_loaded')
        window.$chatwoot.toggle('open')
        return
      }

      window.chatwootSettings = { hideMessageBubble: true, position: 'right', locale: 'ru' }

      window.addEventListener('chatwoot:ready', function onReady() {
        window.removeEventListener('chatwoot:ready', onReady)
        // chatwoot:ready guarantees the SDK has installed window.$chatwoot.
        window.$chatwoot?.setUser(userId ?? '', { identifier_hash, name: displayName || '' })
        loadedRef.current = true
        metrica.reachGoal('psychologist_loaded')
        window.$chatwoot?.toggle('open')
      })

      if (window.chatwootSDK?.run) {
        window.chatwootSDK.run({ websiteToken: website_token, baseUrl: base_url })
        return
      }

      const script = document.createElement('script')
      script.async = true
      script.src = `${base_url}/packs/js/sdk.js`
      script.onload = () => {
        try {
          window.chatwootSDK?.run({ websiteToken: website_token, baseUrl: base_url })
        } catch (e) {
          console.error('[chatwoot] SDK run() failed:', e)
        }
      }
      script.onerror = () => {
        metrica.reachGoal('psychologist_failed')
        toast('Не удалось загрузить виджет чата', 'error')
      }
      document.head.appendChild(script)
    } catch {
      toast('Сервис психолога временно недоступен', 'error')
    }
  }, [])

  const open = useCallback(() => {
    metrica.reachGoal('psychologist_click')
    // User is viewing the thread — clear the unread badge eagerly (parent bd-eiw).
    useChatwootStore.getState().markRead()
    init()
  }, [init])

  return { open }
}
